import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  IVacationAwardImportPreviewResponse,
  IVacationAwardImportResult,
  IVacationAwardImportRow,
  IVacationAwardImportRowPreview,
  VacationAwardImportOutcome,
} from "@vmosolutions/oc-interfaces";

import { ICsvIssue, parseCsvFile } from "src/utils/csvFile";
import { VACATION_BID_TEST_ID } from "../testIds";
import { awardsFromCsv } from "../Csv/awardsCsv";
import { importVacationAwards, previewVacationAwardImport } from "../helpers";

/**
 * Imports a finished award file into the package, for a bid that was run outside Vmo.
 *
 * The file is read here and the rows are sent for the server to resolve, as every import in this
 * screen works. Nothing is written until the admin has seen what the import would do.
 */

interface IProps {
  packageId?: string;
  /** Called once awards have been written, so the rest of the screen can reload. */
  onImported?: () => void;
}

const OUTCOME_LABEL: Record<VacationAwardImportOutcome, string> = {
  [VacationAwardImportOutcome.Ready]: "Ready",
  [VacationAwardImportOutcome.Unmatched]: "No crew member with this employee number",
  [VacationAwardImportOutcome.Inactive]: "Not an active crew member",
  [VacationAwardImportOutcome.Ambiguous]: "More than one crew member has this employee number",
  [VacationAwardImportOutcome.NotInPackage]: "Not in this package",
  [VacationAwardImportOutcome.Blocked]: "Cannot be imported",
};

const name = (row: IVacationAwardImportRowPreview): string =>
  [row.lastName, row.firstName].filter(Boolean).join(", ") || "-";

export const ImportAwardsTab = ({ packageId, onImported }: IProps): JSX.Element => {
  const dispatch = useDispatch();
  const fileInput = useRef<HTMLInputElement>(null);
  /** Which file read is the current one, so a slow read cannot overwrite a newer file's rows. */
  const readCount = useRef<number>(0);

  const [fileName, setFileName] = useState<string>("");
  const [rows, setRows] = useState<IVacationAwardImportRow[]>([]);
  const [issues, setIssues] = useState<ICsvIssue[]>([]);
  const [fatal, setFatal] = useState<string>("");
  const [preview, setPreview] = useState<IVacationAwardImportPreviewResponse | null>(null);
  const [result, setResult] = useState<IVacationAwardImportResult | null>(null);
  const [skipChecks, setSkipChecks] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);

  const clear = (): void => {
    setRows([]);
    setIssues([]);
    setFatal("");
    setPreview(null);
    setResult(null);
  };

  const chooseFile = useCallback(async (file: File): Promise<void> => {
    const read = (readCount.current += 1);
    clear();
    setFileName(file.name);
    const parsed = awardsFromCsv(await parseCsvFile(file));
    // Another file was chosen while this one was being read, so this one's rows are stale and
    // would otherwise land on top of the newer file's.
    if (read !== readCount.current) return;
    setIssues(parsed.issues);
    if (parsed.fatal) {
      setFatal(parsed.fatal);
      return;
    }
    setRows(parsed.rows);
  }, []);

  const runPreview = useCallback(
    async (skip: boolean): Promise<void> => {
      if (!packageId || !rows.length) return;
      setBusy(true);
      setResult(null);
      try {
        const checked = await previewVacationAwardImport(packageId, rows, skip, {
          dispatch,
          errorMessage: "Unable to check the award file",
        });
        setPreview(checked);
        setFatal(checked ? "" : "The award file could not be checked. Try again in a moment.");
      } finally {
        setBusy(false);
      }
    },
    [dispatch, packageId, rows]
  );

  const apply = useCallback(async (): Promise<void> => {
    if (!packageId || !rows.length) return;
    setBusy(true);
    try {
      const applied = await importVacationAwards(packageId, rows, skipChecks, {
        dispatch,
        errorMessage: "Unable to import the awards",
        successMessage: "Awards imported",
      });
      if (applied) {
        // Re-checked first: `runPreview` clears the last result, and this one it has not invalidated.
        await runPreview(skipChecks);
        setResult(applied);
        onImported?.();
      }
    } finally {
      setBusy(false);
    }
  }, [dispatch, packageId, rows, skipChecks, runPreview, onImported]);

  const counts = useMemo(() => {
    const tally: Partial<Record<VacationAwardImportOutcome, number>> = {};
    for (const row of preview?.rows ?? []) tally[row.outcome] = (tally[row.outcome] ?? 0) + 1;
    return tally;
  }, [preview]);

  const ready = counts[VacationAwardImportOutcome.Ready] ?? 0;
  const attention = (preview?.rows ?? []).filter((row) => row.outcome !== VacationAwardImportOutcome.Ready);
  // A week the file cuts short becomes that crew member's short block.
  const shortened = (preview?.rows ?? []).filter(
    (row) => row.outcome === VacationAwardImportOutcome.Ready && row.blocks.some((block) => block.trimmedToDays)
  ).length;
  const canApply = !!preview && !preview.refusedReason && ready > 0 && !busy;

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: "0.9rem", opacity: 0.8 }}>
        For a bid awarded outside Vmo. Import the crew entitlements first, then this. Nothing is written until you
        choose to import.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="outlined"
          disabled={busy}
          data-testid={VACATION_BID_TEST_ID.importAwardsCsvButton}
          onClick={() => fileInput.current?.click()}
        >
          Choose award file
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          hidden
          data-testid={VACATION_BID_TEST_ID.importAwardsCsvInput}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Cleared so choosing the same file again still fires a change.
            event.target.value = "";
            if (file) void chooseFile(file);
          }}
        />
        {fileName && (
          <Typography sx={{ fontSize: "0.85rem" }}>
            {fileName} - {rows.length} crew member{rows.length === 1 ? "" : "s"} read
          </Typography>
        )}
        {!!rows.length && !preview && (
          <Button variant="contained" disabled={busy} onClick={() => void runPreview(skipChecks)}>
            Check the file
          </Button>
        )}
      </Box>

      {fatal && <Alert severity="error">{fatal}</Alert>}

      {!!issues.length && (
        <Alert severity="warning">
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
            {issues.length} line{issues.length === 1 ? "" : "s"} could not be read and will not be imported
          </Typography>
          {issues.slice(0, 8).map((issue) => (
            <Typography key={issue.line} sx={{ fontSize: "0.8rem" }}>
              Line {issue.line}: {issue.message}
            </Typography>
          ))}
          {issues.length > 8 && <Typography sx={{ fontSize: "0.8rem" }}>...and {issues.length - 8} more.</Typography>}
        </Alert>
      )}

      {preview?.refusedReason && <Alert severity="error">{preview.refusedReason}</Alert>}

      {!!preview?.held.length && (
        <Alert severity="info" data-testid={VACATION_BID_TEST_ID.importAwardsHeldNotice}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
            Round {preview.held.map((entry) => entry.round).join(" and ")} will not be imported yet
          </Typography>
          {preview.held.map((entry) => (
            <Typography key={entry.round} sx={{ fontSize: "0.8rem" }}>
              {entry.reason}
            </Typography>
          ))}
          <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
            Import what you can now, publish, then import this same file again for the rest.
          </Typography>
        </Alert>
      )}

      {preview?.entitlementsMissing && !preview.refusedReason && (
        <Alert severity="warning">
          No crew member in this package has a day entitlement, so the weeks cannot be checked against what anyone is
          owed.
        </Alert>
      )}

      {preview && !preview.refusedReason && (
        <Alert
          severity={attention.length ? "warning" : "success"}
          data-testid={VACATION_BID_TEST_ID.importAwardsSummary}
        >
          {ready} of {preview.rows.length} crew members are ready to import
          {attention.length ? `, ${attention.length} need attention` : ""}.
          {!!shortened &&
            ` ${shortened} of them ${shortened === 1 ? "has a week" : "have weeks"} cut short in the file, which ` +
              `${shortened === 1 ? "becomes their short block" : "become their short blocks"}.`}
          {!!preview.omitted.length &&
            ` ${preview.omitted.length} crew members in the package are not in the file; their awards are left alone.`}
        </Alert>
      )}

      {!!attention.length && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 1 }}>Needs attention</Typography>
          <Table size="small" data-testid={VACATION_BID_TEST_ID.importAwardsProblems}>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>What is wrong</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attention.slice(0, 50).map((row) => (
                <TableRow key={row.employeeId}>
                  <TableCell>{row.employeeId}</TableCell>
                  <TableCell>{name(row)}</TableCell>
                  <TableCell>{row.problems.length ? row.problems.join(" ") : OUTCOME_LABEL[row.outcome]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {attention.length > 50 && (
            <Typography sx={{ fontSize: "0.8rem", mt: 1 }}>
              ...and {attention.length - 50} more. Fix the file and check it again.
            </Typography>
          )}
        </Box>
      )}

      {preview && !preview.refusedReason && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            disabled={!canApply}
            data-testid={VACATION_BID_TEST_ID.importAwardsApplyButton}
            onClick={() => void apply()}
          >
            {busy ? "Importing..." : `Import ${ready} crew member${ready === 1 ? "" : "s"}`}
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={skipChecks}
                disabled={busy}
                onChange={(event) => {
                  setSkipChecks(event.target.checked);
                  void runPreview(event.target.checked);
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.85rem" }}>
                Import even where the weeks do not add up to what a crew member is owed
              </Typography>
            }
          />
        </Box>
      )}

      {result && (
        <Alert severity="success" data-testid={VACATION_BID_TEST_ID.importAwardsResult}>
          Imported {result.applied} crew member{result.applied === 1 ? "" : "s"}: {result.awarded} week
          {result.awarded === 1 ? "" : "s"} awarded, {result.replaced} replaced, {result.placed} leftover-day placement
          {result.placed === 1 ? "" : "s"}.{!!result.skipped.length && ` ${result.skipped.length} skipped.`}
        </Alert>
      )}
    </Box>
  );
};
